'use client';

import { useEffect, useRef, useState, useTransition } from 'react';

import { Button } from '~/ui/components/button';
import { Column, Grid } from '~/ui/components/grid';
import { Breakpoint } from '~/ui/styles/grid';

import styles from './list.module.scss';

export interface ListProps {
  totalPages: number;
  fetchItemsAction: (props: { page: number }) => Promise<React.ReactNode[]>;
  itemSkeleton: React.ReactNode;
  loadType: 'auto' | 'manual';
  loadMoreButtonText?: string;
}

export function List({
  totalPages,
  fetchItemsAction,
  itemSkeleton,
  loadType,
  loadMoreButtonText,
}: ListProps) {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<React.ReactNode[]>([]);
  const [isLoading, startTransition] = useTransition();

  return (
    <>
      {items}
      {isLoading &&
        Array.from({ length: 50 }).map((_, i) => (
          <div className={styles.item} key={i}>
            {itemSkeleton}
          </div>
        ))}
      {page < totalPages && !isLoading ? (
        <Loader
          type={loadType}
          // We want to increment the page no matter what. After the component rerenders with the
          //  new page value, the transition will run and start an asynchronous render with the
          //  items from the new page.
          loadMoreButtonText={loadMoreButtonText}
          onLoadMore={() => {
            setPage(prev => prev + 1);
            startTransition(async () => {
              const newItems = await fetchItemsAction({ page });
              setItems(prev => [...prev, ...newItems]);
            });
          }}
        />
      ) : null}
    </>
  );
}

interface LoaderProps {
  onLoadMore: () => void;
  type: 'auto' | 'manual';
  loadMoreButtonText?: string;
}

function Loader({ onLoadMore, type, loadMoreButtonText }: LoaderProps) {
  const autoLoaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const autoLoader = autoLoaderRef.current;
    if (!autoLoader) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(autoLoader);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  switch (type) {
    case 'auto':
      return <div ref={autoLoaderRef} />;

    case 'manual':
      return (
        <Grid>
          <Column offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}>
            <Button className={styles.loadMoreButton} onClick={onLoadMore}>
              {loadMoreButtonText ? loadMoreButtonText : 'Load more'}
            </Button>
          </Column>
        </Grid>
      );
  }
}
